/**
 * The video composition — the one data shape the timeline editor, the
 * renderer, the exporter and the templates all agree on.
 *
 * It is persisted verbatim on a `video-edit` canvas node, so it is a data
 * format: add fields as optional, never rename, and bump `version` (with a
 * migration in `normalizeComposition`) if a field changes meaning.
 *
 * Times are seconds (floats). Positions are normalised to the frame (0..1,
 * x across, y down) so the same composition renders at any resolution and
 * survives an aspect change. Font sizes are a fraction of the frame HEIGHT.
 *
 * Everything in this file is pure — no DOM — so it is unit-tested directly.
 */

export const COMPOSITION_VERSION = 1;

export type AspectRatio = '9:16' | '1:1' | '4:5' | '16:9';

export const ASPECT_SIZES: Record<AspectRatio, { width: number; height: number }> = {
    '9:16': { width: 1080, height: 1920 },
    '1:1': { width: 1080, height: 1080 },
    '4:5': { width: 1080, height: 1350 },
    '16:9': { width: 1920, height: 1080 },
};

export const ASPECT_RATIOS: AspectRatio[] = ['9:16', '4:5', '1:1', '16:9'];

export type TransitionType =
    /**
     * Opens on the picture itself: a quick lift from 85% exposure with a
     * slight scale-in. The first frame is the platform's cover, so a reel
     * must never start on black — this is the default opener.
     */
    | 'soft-in'
    | 'crossfade'
    | 'fade-black'
    | 'fade-white'
    | 'slide-left'
    | 'slide-up'
    | 'zoom'
    | 'blur'
    | 'wipe'
    /** The incoming clip opens through a circle growing from the centre. */
    | 'iris'
    /** A quick white flash at the cut: out-going blows out, incoming comes out of white. */
    | 'flash'
    /** A fast horizontal whip-pan: both pictures slide, smeared with motion blur. */
    | 'whip';

export const TRANSITION_TYPES: TransitionType[] = [
    'soft-in', 'crossfade', 'fade-black', 'fade-white', 'slide-left', 'slide-up', 'zoom', 'blur', 'wipe',
    'iris', 'flash', 'whip',
];

/** The length each transition reads best at — used when one is first chosen. */
export const TRANSITION_DEFAULT_SECONDS: Record<TransitionType, number> = {
    'soft-in': 0.35,
    crossfade: 0.7,
    'fade-black': 0.8,
    'fade-white': 0.7,
    'slide-left': 0.5,
    'slide-up': 0.5,
    zoom: 0.45,
    blur: 0.7,
    wipe: 0.8,
    iris: 0.7,
    flash: 0.25,
    whip: 0.4,
};

/** Transitions that make sense on the very first clip (they come out of the background, not a clip). */
export const OPENING_TRANSITIONS: TransitionType[] = ['soft-in', 'fade-black', 'fade-white', 'iris', 'flash'];

/** How the first clip opens unless someone chose otherwise. */
export const OPENER: ClipTransition = { type: 'soft-in', duration: 0.35 };

export interface ClipTransition {
    type: TransitionType;
    /** Seconds the incoming clip overlaps the outgoing one. */
    duration: number;
}

export type MotionType = 'none' | 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right' | 'pan-up';
export const MOTION_TYPES: MotionType[] = ['none', 'zoom-in', 'zoom-out', 'pan-left', 'pan-right', 'pan-up'];

export interface ClipMotion {
    type: MotionType;
    /** 0..1 — how far the camera travels over the clip. */
    intensity: number;
}

export type ClipFit = 'cover' | 'contain';

export type MainClipKind = 'video' | 'image' | 'color';

/** Playback-rate bounds for video clips (slow motion to double speed). */
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 2;

/**
 * A clip on the main (picture) track. Main clips play one after another.
 *
 * `kind: 'color'` is a solid card with no media (a title or end frame): it
 * has an empty `src` and draws `color` across the whole frame.
 */
export interface MainClip {
    id: string;
    kind: MainClipKind;
    /** Media URL; '' for a colour card. */
    src: string;
    assetUuid?: string;
    /** Poster for the timeline strip and the media bin. */
    thumbnailUrl?: string;
    /** Derived by `layoutMainTrack` — never set by hand. */
    start: number;
    duration: number;
    /** Seconds into the source the clip begins at (video only). */
    trimIn: number;
    /** Length of the source video, when known — bounds trimming. */
    sourceDuration?: number;
    /**
     * Video only: playback rate, MIN_SPEED..MAX_SPEED (default 1). 0.5 is
     * half-speed slow motion — the clip then consumes `duration * speed`
     * seconds of its source.
     */
    speed?: number;
    /** Colour cards only: the fill, '#RRGGBB'. */
    color?: string;
    volume: number;
    muted: boolean;
    fit: ClipFit;
    motion?: ClipMotion;
    /** How this clip arrives over the previous one. */
    transitionIn?: ClipTransition;
    label?: string;
}

export type LayerAnim =
    | 'none'
    | 'fade'
    | 'rise'
    | 'fall'
    | 'blur'
    | 'scale'
    | 'typewriter'
    | 'letter-stagger'
    | 'slide-left'
    | 'wipe'
    /** Text: each line slides up from behind an invisible mask at its baseline (out: back down). */
    | 'mask-up'
    /** Text: words arrive one after another with a short fade and rise (out: last word leaves first). */
    | 'word-stagger'
    /** Text: letter spacing tightens from wide to the set value while fading in (out: widens away). */
    | 'tracking-in';

export const LAYER_ANIMS: LayerAnim[] = [
    'none', 'fade', 'rise', 'fall', 'blur', 'scale', 'typewriter', 'letter-stagger', 'slide-left', 'wipe',
    'mask-up', 'word-stagger', 'tracking-in',
];

/** Animations that only mean something on text (other layers fade instead). */
export const TEXT_ONLY_ANIMS: LayerAnim[] = ['typewriter', 'letter-stagger', 'mask-up', 'word-stagger', 'tracking-in'];

/** Animations that only exist as an entrance. */
export const IN_ONLY_ANIMS: LayerAnim[] = ['typewriter', 'letter-stagger'];

interface LayerBase {
    id: string;
    start: number;
    duration: number;
    /** Centre of the layer, normalised (0..1). */
    x: number;
    y: number;
    /** Width of the layer box as a fraction of the frame width. */
    width: number;
    /** 0..1 */
    opacity: number;
    /** Degrees, clockwise. */
    rotation?: number;
    animIn: LayerAnim;
    animOut: LayerAnim;
    /** Seconds each of the in/out animations takes. */
    animDuration: number;
}

export interface TextBackground {
    style: 'pill' | 'box';
    color: string;
    opacity: number;
    /** Padding in em of the font size. */
    paddingX: number;
    paddingY: number;
}

export interface TextShadow {
    color: string;
    opacity: number;
    /** Blur as a fraction of the font size. */
    blur: number;
    /** Vertical offset as a fraction of the font size. */
    offsetY: number;
}

export interface TextStroke {
    color: string;
    /** Outline thickness as a fraction of the font size (0.03 is a crisp outline). */
    width: number;
}

export interface TextLayer extends LayerBase {
    kind: 'text';
    text: string;
    align: 'left' | 'center' | 'right';
    fontFamily: string;
    /** Fraction of the frame height. */
    fontSize: number;
    fontWeight: number;
    fontStyle?: 'normal' | 'italic';
    /** em */
    letterSpacing: number;
    /** Multiple of the font size. */
    lineHeight: number;
    color: string;
    uppercase: boolean;
    background?: TextBackground;
    shadow?: TextShadow;
    /** An outline around the letters. */
    stroke?: TextStroke;
    /** 0..1 opacity of the letter fill (default 1). 0 with a stroke = outline-only type. */
    fillOpacity?: number;
    /** Which preset produced this layer, for the inspector's highlight. */
    stylePreset?: string;
}

export interface ImageLayer extends LayerBase {
    kind: 'image';
    src: string;
    assetUuid?: string;
    /** Natural aspect (w/h) when known, so layout does not wait on decode. */
    aspect?: number;
}

export interface ShapeLayer extends LayerBase {
    kind: 'shape';
    shape: 'rect' | 'line' | 'scrim';
    color: string;
    /** Height as a fraction of the frame height. */
    height: number;
    /** Corner radius as a fraction of the frame width. */
    radius?: number;
    /** Scrim only: where the colour is solid. */
    direction?: 'to-top' | 'to-bottom';
}

export type OverlayLayer = TextLayer | ImageLayer | ShapeLayer;

export interface AudioClip {
    id: string;
    src: string;
    name?: string;
    start: number;
    duration: number;
    trimIn: number;
    sourceDuration?: number;
    volume: number;
    fadeIn: number;
    fadeOut: number;
}

export interface MainTrack {
    id: string;
    kind: 'main';
    clips: MainClip[];
}

export interface OverlayTrack {
    id: string;
    kind: 'overlay';
    hidden?: boolean;
    layers: OverlayLayer[];
}

export interface AudioTrack {
    id: string;
    kind: 'audio';
    muted?: boolean;
    clips: AudioClip[];
}

export type CompositionTrack = MainTrack | OverlayTrack | AudioTrack;

export interface VideoComposition {
    version: number;
    width: number;
    height: number;
    fps: number;
    aspect: AspectRatio;
    /** Fill behind contained media and before the first frame. */
    background: string;
    /** Exactly one main track (first); overlays draw in array order; then audio. */
    tracks: CompositionTrack[];
}

/** Anything placed on the timeline. */
export type TimelineItem = MainClip | OverlayLayer | AudioClip;

export const MIN_ITEM_DURATION = 0.2;
export const MIN_TRANSITION = 0.1;

// ─── ids ──────────────────────────────────────────────────────────────

let idCounter = 0;
export function createId(prefix = 'it'): string {
    idCounter = (idCounter + 1) % 1_000_000;
    const rand = Math.random().toString(36).slice(2, 8);
    return `${prefix}-${rand}${idCounter.toString(36)}`;
}

// ─── construction ─────────────────────────────────────────────────────

export function createEmptyComposition(aspect: AspectRatio = '9:16', fps = 30): VideoComposition {
    const { width, height } = ASPECT_SIZES[aspect];
    return {
        version: COMPOSITION_VERSION,
        width,
        height,
        fps,
        aspect,
        background: '#0E0E0C',
        tracks: [
            { id: 'main', kind: 'main', clips: [] },
            { id: createId('ov'), kind: 'overlay', layers: [] },
            { id: createId('au'), kind: 'audio', clips: [] },
        ],
    };
}

export function createMainClip(input: {
    kind: MainClipKind;
    src: string;
    assetUuid?: string;
    thumbnailUrl?: string;
    duration?: number;
    sourceDuration?: number;
    trimIn?: number;
    /** Video only. */
    speed?: number;
    /** Colour cards only. */
    color?: string;
    motion?: ClipMotion;
    transitionIn?: ClipTransition;
    label?: string;
}): MainClip {
    const isVideo = input.kind === 'video';
    const sourceDuration = isVideo ? input.sourceDuration : undefined;
    const trimIn = isVideo ? Math.max(0, input.trimIn ?? 0) : 0;
    const speed = isVideo && input.speed !== undefined ? clampSpeed(input.speed) : 1;
    let duration = input.duration ?? (isVideo ? (sourceDuration ?? 5) / speed : 3);
    if (sourceDuration) duration = Math.min(duration, Math.max(MIN_ITEM_DURATION, (sourceDuration - trimIn) / speed));
    const clip: MainClip = {
        id: createId('clip'),
        kind: input.kind,
        src: input.kind === 'color' ? '' : input.src,
        assetUuid: input.assetUuid,
        thumbnailUrl: input.thumbnailUrl,
        start: 0,
        duration: Math.max(MIN_ITEM_DURATION, duration),
        trimIn,
        sourceDuration,
        volume: 1,
        muted: input.kind === 'image',
        fit: 'cover',
        motion: input.motion ?? (input.kind === 'image' ? { type: 'zoom-in', intensity: 0.4 } : undefined),
        transitionIn: input.transitionIn,
        label: input.label,
    };
    if (speed !== 1) clip.speed = speed;
    if (input.kind === 'color') {
        clip.color = normalizeHex(input.color) ?? '#000000';
        clip.muted = true;
    }
    return clip;
}

/** A solid-colour card on the main track — a title or end frame with no media. */
export function createColorClip(input: {
    color: string;
    duration?: number;
    transitionIn?: ClipTransition;
    label?: string;
}): MainClip {
    return createMainClip({
        kind: 'color',
        src: '',
        color: input.color,
        duration: input.duration ?? 2.5,
        transitionIn: input.transitionIn,
        label: input.label,
    });
}

// ─── clip speed ───────────────────────────────────────────────────────

export function clampSpeed(v: number): number {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return 1;
    return Math.round(Math.min(MAX_SPEED, Math.max(MIN_SPEED, n)) * 1000) / 1000;
}

/** The rate a clip plays its source at: its speed for video, 1 for everything else. */
export function clipSpeed(clip: { kind?: MainClipKind; speed?: number }): number {
    if (clip.kind !== 'video' || clip.speed === undefined) return 1;
    return clampSpeed(clip.speed);
}

/** Seconds of source a clip consumes on screen. */
export function sourceSpan(clip: { kind?: MainClipKind; speed?: number; duration: number }): number {
    return clip.duration * clipSpeed(clip);
}

/** The longest a video clip can run from its trimIn at its speed (Infinity when the source length is unknown). */
export function maxClipDuration(clip: Pick<MainClip, 'kind' | 'speed' | 'trimIn' | 'sourceDuration'>): number {
    if (clip.kind !== 'video' || !clip.sourceDuration) return Infinity;
    return Math.max(MIN_ITEM_DURATION, (clip.sourceDuration - clip.trimIn) / clipSpeed(clip));
}

/**
 * Change a video clip's speed. The clip keeps the same stretch of source —
 * slowing a 2 s moment to 0.5x makes it 4 s on screen, rippling what follows.
 */
export function setClipSpeed(comp: VideoComposition, id: string, speed: number): VideoComposition {
    const found = findItem(comp, id);
    if (!found || found.track.kind !== 'main') return comp;
    const clip = found.item as MainClip;
    if (clip.kind !== 'video') return comp;
    const next = clampSpeed(speed);
    let duration = sourceSpan(clip) / next;
    if (clip.sourceDuration) duration = Math.min(duration, (clip.sourceDuration - clip.trimIn) / next);
    return updateItem<MainClip>(comp, id, {
        speed: next === 1 ? undefined : next,
        duration: round(Math.max(MIN_ITEM_DURATION, duration)),
    });
}

function normalizeHex(v: unknown): string | undefined {
    if (typeof v !== 'string') return undefined;
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v.trim());
    if (!m) return undefined;
    const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
    return `#${h.toUpperCase()}`;
}

// ─── track access ─────────────────────────────────────────────────────

export function mainTrack(comp: VideoComposition): MainTrack {
    const t = comp.tracks.find((x) => x.kind === 'main') as MainTrack | undefined;
    return t ?? { id: 'main', kind: 'main', clips: [] };
}

export function overlayTracks(comp: VideoComposition): OverlayTrack[] {
    return comp.tracks.filter((t) => t.kind === 'overlay') as OverlayTrack[];
}

export function audioTracks(comp: VideoComposition): AudioTrack[] {
    return comp.tracks.filter((t) => t.kind === 'audio') as AudioTrack[];
}

export function trackItems(track: CompositionTrack): TimelineItem[] {
    if (track.kind === 'main') return track.clips;
    if (track.kind === 'overlay') return track.layers;
    return track.clips;
}

function withTrackItems(track: CompositionTrack, items: TimelineItem[]): CompositionTrack {
    if (track.kind === 'main') return { ...track, clips: items as MainClip[] };
    if (track.kind === 'overlay') return { ...track, layers: items as OverlayLayer[] };
    return { ...track, clips: items as AudioClip[] };
}

export function findItem(
    comp: VideoComposition,
    id: string,
): { item: TimelineItem; track: CompositionTrack; index: number } | null {
    for (const track of comp.tracks) {
        const items = trackItems(track);
        const index = items.findIndex((i) => i.id === id);
        if (index >= 0) return { item: items[index], track, index };
    }
    return null;
}

export function itemEnd(item: { start: number; duration: number }): number {
    return item.start + item.duration;
}

// ─── main-track layout (ripple) ───────────────────────────────────────

/** Longest a transition into `clip` may be, given its neighbour. */
export function maxTransitionDuration(prev: MainClip | undefined, clip: MainClip): number {
    if (!prev) return Math.max(0, clip.duration * 0.5);
    return Math.max(0, Math.min(prev.duration, clip.duration) * 0.5);
}

/**
 * Lay the main clips end to end. A clip with a transition starts that many
 * seconds before the previous clip ends — the overlap is the transition.
 * Transition durations are clamped so they never eat a whole clip.
 */
export function layoutMainTrack(clips: MainClip[]): MainClip[] {
    let cursor = 0;
    return clips.map((c, i) => {
        const prev = i > 0 ? clips[i - 1] : undefined;
        let transitionIn = c.transitionIn;
        let overlap = 0;
        if (transitionIn) {
            const max = maxTransitionDuration(prev, c);
            const duration = Math.min(Math.max(MIN_TRANSITION, transitionIn.duration), max);
            transitionIn = duration >= MIN_TRANSITION ? { ...transitionIn, duration } : undefined;
            // The first clip has nothing to overlap; its transition fades in from the background.
            overlap = prev && transitionIn ? transitionIn.duration : 0;
        }
        const start = Math.max(0, cursor - overlap);
        cursor = start + c.duration;
        return { ...c, start: round(start), transitionIn };
    });
}

export function mainDuration(comp: VideoComposition): number {
    const clips = mainTrack(comp).clips;
    if (!clips.length) return 0;
    const last = clips[clips.length - 1];
    return itemEnd(last);
}

/** The length of the finished video: the picture, or the last overlay if it runs longer. */
export function totalDuration(comp: VideoComposition): number {
    let end = mainDuration(comp);
    for (const t of overlayTracks(comp)) for (const l of t.layers) end = Math.max(end, itemEnd(l));
    return round(end);
}

export function frameCount(comp: VideoComposition): number {
    return Math.max(1, Math.ceil(totalDuration(comp) * comp.fps - 1e-6));
}

export function frameDuration(comp: VideoComposition): number {
    return 1 / comp.fps;
}

/** Snap a time to the composition's frame grid. */
export function quantizeToFrame(comp: VideoComposition, t: number): number {
    return Math.round(t * comp.fps) / comp.fps;
}

// ─── what is on screen at t ───────────────────────────────────────────

export interface MainFrameState {
    /** The clip that owns the frame (the incoming one during a transition). */
    current: MainClip | null;
    currentIndex: number;
    /** The outgoing clip while a transition runs. */
    previous: MainClip | null;
    /** 0..1 progress of `current.transitionIn`, or 1 when none is running. */
    transitionProgress: number;
}

/** The main clip at time t (the incoming one when two overlap). */
export function clipAtTime(clips: MainClip[], t: number): { clip: MainClip; index: number } | null {
    for (let i = clips.length - 1; i >= 0; i--) {
        const c = clips[i];
        if (t >= c.start - 1e-9 && t < itemEnd(c) - 1e-9) return { clip: c, index: i };
    }
    return null;
}

export function transitionProgress(clip: MainClip, t: number): number {
    const tr = clip.transitionIn;
    if (!tr || tr.duration <= 0) return 1;
    return clamp01((t - clip.start) / tr.duration);
}

export function mainFrameState(clips: MainClip[], t: number): MainFrameState {
    const hit = clipAtTime(clips, t);
    if (!hit) return { current: null, currentIndex: -1, previous: null, transitionProgress: 1 };
    const { clip, index } = hit;
    const p = transitionProgress(clip, t);
    const prev = index > 0 ? clips[index - 1] : null;
    const inTransition = !!clip.transitionIn && p < 1;
    return {
        current: clip,
        currentIndex: index,
        previous: inTransition && prev && t < itemEnd(prev) + 1e-9 ? prev : null,
        transitionProgress: p,
    };
}

/** Where in its source a clip is at composition time t (seconds), at the clip's speed. */
export function sourceTimeAt(
    clip: { start: number; trimIn: number; duration: number; kind?: MainClipKind; speed?: number },
    t: number,
): number {
    const local = Math.min(Math.max(0, t - clip.start), clip.duration);
    return clip.trimIn + local * clipSpeed(clip);
}

export function activeLayers(comp: VideoComposition, t: number): OverlayLayer[] {
    const out: OverlayLayer[] = [];
    for (const track of overlayTracks(comp)) {
        if (track.hidden) continue;
        for (const l of track.layers) if (t >= l.start - 1e-9 && t < itemEnd(l) - 1e-9) out.push(l);
    }
    return out;
}

// ─── editing ──────────────────────────────────────────────────────────

function mapTracks(
    comp: VideoComposition,
    fn: (track: CompositionTrack) => CompositionTrack,
): VideoComposition {
    return normalizeComposition({ ...comp, tracks: comp.tracks.map(fn) });
}

export function updateItem<T extends TimelineItem>(
    comp: VideoComposition,
    id: string,
    patch: Partial<T>,
): VideoComposition {
    return mapTracks(comp, (track) => {
        const items = trackItems(track);
        if (!items.some((i) => i.id === id)) return track;
        return withTrackItems(track, items.map((i) => (i.id === id ? ({ ...i, ...patch } as TimelineItem) : i)));
    });
}

/** Split an item at composition time t. Returns the composition and the new right-hand id. */
export function splitItemAt(
    comp: VideoComposition,
    id: string,
    t: number,
): { comp: VideoComposition; newId: string | null } {
    const found = findItem(comp, id);
    if (!found) return { comp, newId: null };
    const { item } = found;
    const local = t - item.start;
    if (local < MIN_ITEM_DURATION - 1e-9 || item.duration - local < MIN_ITEM_DURATION - 1e-9) {
        return { comp, newId: null };
    }
    const newId = createId(found.track.kind === 'main' ? 'clip' : found.track.kind === 'audio' ? 'aud' : 'ly');
    const left: any = { ...item, duration: round(local) };
    const right: any = { ...item, id: newId, start: round(item.start + local), duration: round(item.duration - local) };
    if (found.track.kind === 'main') {
        const clip = item as MainClip;
        right.trimIn = round(clip.trimIn + local * clipSpeed(clip));
        // A cut inside a clip is a cut, not a transition.
        right.transitionIn = undefined;
    } else if (found.track.kind === 'audio') {
        const clip = item as AudioClip;
        right.trimIn = round(clip.trimIn + local);
        left.fadeOut = 0;
        right.fadeIn = 0;
    } else {
        left.animOut = 'none';
        right.animIn = 'none';
    }
    const next = mapTracks(comp, (track) => {
        if (track.id !== found.track.id) return track;
        const items = trackItems(track).slice();
        items.splice(found.index, 1, left, right);
        return withTrackItems(track, items);
    });
    return { comp: next, newId };
}

/**
 * Remove an item. On the main track the gap closes (ripple); elsewhere the
 * item just leaves.
 */
export function deleteItem(comp: VideoComposition, id: string): VideoComposition {
    return mapTracks(comp, (track) => {
        const items = trackItems(track);
        if (!items.some((i) => i.id === id)) return track;
        const remaining = items.filter((i) => i.id !== id);
        if (track.kind === 'main' && remaining.length) {
            // The clip that now opens the reel has nothing to transition from.
            const first = remaining[0] as MainClip;
            if (first.transitionIn && !OPENING_TRANSITIONS.includes(first.transitionIn.type)) {
                remaining[0] = { ...first, transitionIn: undefined };
            }
        }
        return withTrackItems(track, remaining);
    });
}

/** Ripple delete — alias kept for readability at call sites. */
export const rippleDelete = deleteItem;

export function duplicateItem(comp: VideoComposition, id: string): { comp: VideoComposition; newId: string | null } {
    const found = findItem(comp, id);
    if (!found) return { comp, newId: null };
    const newId = createId(found.track.kind === 'main' ? 'clip' : found.track.kind === 'audio' ? 'aud' : 'ly');
    const copy: any = { ...found.item, id: newId };
    if (found.track.kind !== 'main') copy.start = round(itemEnd(found.item));
    const next = mapTracks(comp, (track) => {
        if (track.id !== found.track.id) return track;
        const items = trackItems(track).slice();
        items.splice(found.index + 1, 0, copy);
        return withTrackItems(track, items);
    });
    return { comp: next, newId };
}

/**
 * Trim one edge of an item to a composition time.
 *
 * - main clips ripple: moving the start edge eats into the source (trimIn)
 *   and the clips after it slide; moving the end edge changes duration.
 * - overlays and audio keep their other edge where it is.
 * Video sources bound how far either edge can go; a clip with a speed moves
 * through its source `speed` seconds per second of timeline.
 */
export function trimItem(
    comp: VideoComposition,
    id: string,
    edge: 'start' | 'end',
    time: number,
): VideoComposition {
    const found = findItem(comp, id);
    if (!found) return comp;
    const item = found.item as any;
    const kind = found.track.kind;
    const hasSource = kind === 'audio' || (kind === 'main' && item.kind === 'video');
    const speed = kind === 'main' ? clipSpeed(item) : 1;
    const end = itemEnd(item);
    const patch: any = {};
    if (edge === 'end') {
        let duration = Math.max(MIN_ITEM_DURATION, time - item.start);
        if (hasSource && item.sourceDuration) duration = Math.min(duration, (item.sourceDuration - item.trimIn) / speed);
        patch.duration = round(duration);
    } else {
        let newStart = Math.min(time, end - MIN_ITEM_DURATION);
        let delta = newStart - item.start;
        if (hasSource) {
            // Cannot reveal source before its beginning.
            if (item.trimIn + delta * speed < 0) delta = -item.trimIn / speed;
            patch.trimIn = round(item.trimIn + delta * speed);
        } else if (kind !== 'main') {
            if (item.start + delta < 0) delta = -item.start;
        }
        newStart = item.start + delta;
        if (kind === 'main') {
            // Ripple: the clip keeps its slot, it just gets shorter or longer.
            patch.duration = round(Math.max(MIN_ITEM_DURATION, item.duration - delta));
        } else {
            patch.start = round(Math.max(0, newStart));
            patch.duration = round(Math.max(MIN_ITEM_DURATION, end - Math.max(0, newStart)));
        }
    }
    return updateItem(comp, id, patch);
}

/** Set a clip's duration directly (inspector field), honouring its source. */
export function setItemDuration(comp: VideoComposition, id: string, duration: number): VideoComposition {
    const found = findItem(comp, id);
    if (!found) return comp;
    return trimItem(comp, id, 'end', found.item.start + duration);
}

/** Move an overlay or audio item to a new start, optionally onto another track of its kind. */
export function moveItem(
    comp: VideoComposition,
    id: string,
    start: number,
    targetTrackId?: string,
): VideoComposition {
    const found = findItem(comp, id);
    if (!found || found.track.kind === 'main') return comp;
    const moved = { ...found.item, start: round(Math.max(0, start)) };
    const target = targetTrackId
        ? comp.tracks.find((t) => t.id === targetTrackId && t.kind === found.track.kind)
        : undefined;
    if (!target || target.id === found.track.id) return updateItem(comp, id, { start: moved.start });
    return mapTracks(comp, (track) => {
        if (track.id === found.track.id) {
            return withTrackItems(track, trackItems(track).filter((i) => i.id !== id));
        }
        if (track.id === target.id) return withTrackItems(track, [...trackItems(track), moved]);
        return track;
    });
}

/** Reorder a main clip to a new index. */
export function reorderMainClip(comp: VideoComposition, id: string, toIndex: number): VideoComposition {
    return mapTracks(comp, (track) => {
        if (track.kind !== 'main') return track;
        const clips = track.clips.slice();
        const from = clips.findIndex((c) => c.id === id);
        if (from < 0) return track;
        const [clip] = clips.splice(from, 1);
        const to = Math.max(0, Math.min(clips.length, toIndex));
        clips.splice(to, 0, clip);
        return { ...track, clips };
    });
}

/** Insert main clips at an index (end when omitted). */
export function insertMainClips(comp: VideoComposition, clips: MainClip[], atIndex?: number): VideoComposition {
    return mapTracks(comp, (track) => {
        if (track.kind !== 'main') return track;
        const next = track.clips.slice();
        next.splice(atIndex ?? next.length, 0, ...clips);
        return { ...track, clips: next };
    });
}

/** Index a clip dropped at time t should take on the main track. */
export function mainInsertIndexAt(clips: MainClip[], t: number): number {
    for (let i = 0; i < clips.length; i++) {
        if (t < clips[i].start + clips[i].duration / 2) return i;
    }
    return clips.length;
}

/**
 * Put an overlay layer on the first overlay track where it does not collide,
 * creating a track (on top) if none has room.
 */
export function addOverlayLayer(comp: VideoComposition, layer: OverlayLayer): VideoComposition {
    const tracks = overlayTracks(comp);
    const fits = (t: OverlayTrack) =>
        t.layers.every((l) => itemEnd(l) <= layer.start + 1e-6 || l.start >= itemEnd(layer) - 1e-6);
    const target = tracks.find(fits);
    if (target) {
        return mapTracks(comp, (t) => (t.id === target.id ? { ...(t as OverlayTrack), layers: [...(t as OverlayTrack).layers, layer] } : t));
    }
    const fresh: OverlayTrack = { id: createId('ov'), kind: 'overlay', layers: [layer] };
    // New overlay tracks sit above the existing ones: after the last overlay track.
    const lastOverlay = comp.tracks.map((t) => t.kind).lastIndexOf('overlay');
    const insertAt = lastOverlay >= 0 ? lastOverlay + 1 : 1;
    const nextTracks = comp.tracks.slice();
    nextTracks.splice(insertAt, 0, fresh);
    return normalizeComposition({ ...comp, tracks: nextTracks });
}

export function addAudioClip(comp: VideoComposition, clip: AudioClip): VideoComposition {
    const tracks = audioTracks(comp);
    const fits = (t: AudioTrack) =>
        t.clips.every((c) => itemEnd(c) <= clip.start + 1e-6 || c.start >= itemEnd(clip) - 1e-6);
    const target = tracks.find(fits);
    if (target) {
        return mapTracks(comp, (t) => (t.id === target.id ? { ...(t as AudioTrack), clips: [...(t as AudioTrack).clips, clip] } : t));
    }
    return normalizeComposition({
        ...comp,
        tracks: [...comp.tracks, { id: createId('au'), kind: 'audio', clips: [clip] }],
    });
}

export function createAudioClip(input: {
    src: string;
    name?: string;
    start?: number;
    duration: number;
    sourceDuration?: number;
    volume?: number;
}): AudioClip {
    return {
        id: createId('aud'),
        src: input.src,
        name: input.name,
        start: input.start ?? 0,
        duration: Math.max(MIN_ITEM_DURATION, input.duration),
        trimIn: 0,
        sourceDuration: input.sourceDuration,
        volume: input.volume ?? 0.8,
        fadeIn: 0.5,
        fadeOut: 1.5,
    };
}

export function setAspect(comp: VideoComposition, aspect: AspectRatio): VideoComposition {
    const { width, height } = ASPECT_SIZES[aspect];
    return { ...comp, aspect, width, height };
}

// ─── snapping ─────────────────────────────────────────────────────────

/** Every edge an item could snap to: zero, the playhead, all other items' edges. */
export function snapPoints(comp: VideoComposition, excludeId: string | null, playhead?: number): number[] {
    const pts = new Set<number>([0]);
    if (playhead !== undefined) pts.add(round(playhead));
    for (const track of comp.tracks) {
        for (const item of trackItems(track)) {
            if (item.id === excludeId) continue;
            pts.add(round(item.start));
            pts.add(round(itemEnd(item)));
        }
    }
    return Array.from(pts).sort((a, b) => a - b);
}

/** The nearest snap point within `threshold` seconds, else t itself. */
export function snapTime(t: number, points: number[], threshold: number): { time: number; snapped: boolean } {
    let best = t;
    let bestDist = threshold;
    for (const p of points) {
        const d = Math.abs(p - t);
        if (d <= bestDist) {
            best = p;
            bestDist = d;
        }
    }
    return { time: best, snapped: best !== t };
}

/** Snap a moving item by whichever of its two edges lands closest to a point. */
export function snapSpan(
    start: number,
    duration: number,
    points: number[],
    threshold: number,
): { start: number; snapped: boolean; at?: number } {
    const a = snapTime(start, points, threshold);
    const b = snapTime(start + duration, points, threshold);
    const da = a.snapped ? Math.abs(a.time - start) : Infinity;
    const db = b.snapped ? Math.abs(b.time - (start + duration)) : Infinity;
    if (da === Infinity && db === Infinity) return { start, snapped: false };
    if (da <= db) return { start: a.time, snapped: true, at: a.time };
    return { start: b.time - duration, snapped: true, at: b.time };
}

// ─── normalisation ────────────────────────────────────────────────────

/**
 * Restore every invariant: one main track first, main clips laid out
 * end to end, durations positive, transitions clamped. Safe on anything
 * read back from storage.
 */
export function normalizeComposition(input: VideoComposition): VideoComposition {
    const aspect: AspectRatio = (input?.aspect && ASPECT_SIZES[input.aspect]) ? input.aspect : '9:16';
    const size = ASPECT_SIZES[aspect];
    const tracks = Array.isArray(input?.tracks) ? input.tracks : [];
    const mains = tracks.filter((t) => t?.kind === 'main') as MainTrack[];
    const mainClips: MainClip[] = mains.flatMap((m) => m.clips || []).map(normalizeMainClip);
    const main: MainTrack = { id: mains[0]?.id || 'main', kind: 'main', clips: layoutMainTrack(mainClips) };
    const rest = tracks
        .filter((t) => t && (t.kind === 'overlay' || t.kind === 'audio'))
        .map((t) => {
            if (t.kind === 'overlay') {
                return {
                    ...t,
                    layers: (t.layers || []).map((l) => normalizeTextStyle({
                        ...l,
                        start: Math.max(0, Number(l.start) || 0),
                        duration: Math.max(MIN_ITEM_DURATION, Number(l.duration) || MIN_ITEM_DURATION),
                    })),
                } as OverlayTrack;
            }
            return {
                ...t,
                clips: ((t as AudioTrack).clips || []).map((c) => ({
                    ...c,
                    start: Math.max(0, Number(c.start) || 0),
                    duration: Math.max(MIN_ITEM_DURATION, Number(c.duration) || MIN_ITEM_DURATION),
                    trimIn: Math.max(0, Number(c.trimIn) || 0),
                })),
            } as AudioTrack;
        });
    // Overlays stay above the main track in array order; audio goes last.
    const overlays = rest.filter((t) => t.kind === 'overlay');
    const audio = rest.filter((t) => t.kind === 'audio');
    return {
        version: COMPOSITION_VERSION,
        width: size.width,
        height: size.height,
        fps: [24, 25, 30, 60].includes(input?.fps) ? input.fps : 30,
        aspect,
        background: input?.background || '#0E0E0C',
        tracks: [main, ...overlays, ...audio],
    };
}

function normalizeMainClip(c: MainClip): MainClip {
    const kind: MainClipKind = c.kind === 'video' || c.kind === 'color' ? c.kind : 'image';
    const out: MainClip = {
        ...c,
        kind,
        src: kind === 'color' ? '' : c.src || '',
        duration: Math.max(MIN_ITEM_DURATION, Number(c.duration) || MIN_ITEM_DURATION),
        // Only a video has a source to be into.
        trimIn: kind === 'video' ? Math.max(0, Number(c.trimIn) || 0) : 0,
        volume: c.volume ?? 1,
        muted: kind === 'color' ? true : !!c.muted,
        fit: c.fit === 'contain' ? 'contain' : 'cover',
    };
    // Speed is a video property, and 1 is stored as no speed at all.
    const speed = kind === 'video' && c.speed !== undefined ? clampSpeed(c.speed) : 1;
    if (speed !== 1) out.speed = speed;
    else delete out.speed;
    if (kind === 'color') out.color = normalizeHex(c.color) ?? '#000000';
    else delete out.color;
    return out;
}

/** Keep a text layer's outline and fill inside their ranges; other layers pass through. */
function normalizeTextStyle(l: OverlayLayer): OverlayLayer {
    if (l.kind !== 'text') return l;
    const out: TextLayer = { ...l };
    if (out.fillOpacity !== undefined) {
        const f = Number(out.fillOpacity);
        out.fillOpacity = Number.isFinite(f) ? clamp01(f) : 1;
    }
    if (out.stroke) {
        const width = Math.max(0, Math.min(0.25, Number(out.stroke.width) || 0));
        if (width > 0) out.stroke = { color: out.stroke.color || out.color || '#FFFFFF', width };
        else delete out.stroke;
    }
    return out;
}

// ─── easing ───────────────────────────────────────────────────────────

export function clamp01(v: number): number {
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

export const easing = {
    linear: (p: number) => p,
    inOutSine: (p: number) => -(Math.cos(Math.PI * p) - 1) / 2,
    inOutCubic: (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2),
    outCubic: (p: number) => 1 - Math.pow(1 - p, 3),
    outQuart: (p: number) => 1 - Math.pow(1 - p, 4),
    outExpo: (p: number) => (p >= 1 ? 1 : 1 - Math.pow(2, -10 * p)),
    inOutExpo: (p: number) => {
        if (p <= 0) return 0;
        if (p >= 1) return 1;
        return p < 0.5 ? Math.pow(2, 20 * p - 10) / 2 : (2 - Math.pow(2, -20 * p + 10)) / 2;
    },
    inOutQuint: (p: number) => (p < 0.5 ? 16 * p ** 5 : 1 - Math.pow(-2 * p + 2, 5) / 2),
};

export function round(v: number): number {
    return Math.round(v * 1000) / 1000;
}

export function formatTime(t: number, fps?: number): string {
    const safe = Math.max(0, t);
    const m = Math.floor(safe / 60);
    const s = Math.floor(safe % 60);
    if (fps) {
        const f = Math.floor((safe - Math.floor(safe)) * fps + 1e-6);
        return `${m}:${String(s).padStart(2, '0')}:${String(f).padStart(2, '0')}`;
    }
    const tenths = Math.floor((safe - Math.floor(safe)) * 10 + 1e-6);
    return `${m}:${String(s).padStart(2, '0')}.${tenths}`;
}
